import React, { useState } from 'react'
import { alpha } from '@mui/material/styles'
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
} from '@mui/material'
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  VideoFile as VideoIcon,
  Description as DocumentIcon,
  Store as StoreIcon,
  Circle as CircleIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  PlayArrow as PlayArrowIcon,
} from '@mui/icons-material'

import { ContentItem } from '../../types/catalog'
import ContentCardPreview from './ContentCardPreview'
import MediaPlaceholder from './MediaPreview'

export const StatusBadge = ({ status }: { status?: string }) => (
  <Chip
    size="small"
    label={status === 'active' ? 'Active' : 'Inactive'}
    icon={<CircleIcon sx={{ fontSize: '10px !important' }} />}
    sx={{ 
      height: 24, 
      fontSize: '0.75rem', 
      fontWeight: 600, 
      bgcolor: (theme) => alpha(theme.palette.background.paper, 0.9),
      color: status === 'active' ? 'success.main' : 'error.main',
      backdropFilter: 'blur(4px)',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      border: 'none',
      '& .MuiChip-icon': {
        color: 'inherit'
      }
    }}
  />
)

export const getPlaceholderIcon = (item: ContentItem) => {
  if (item.has_integration) {
    return <StoreIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5 }} />
  }
  switch (item.type) {
    case 'image': return <ImageIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5 }} />
    case 'video': return <VideoIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5 }} />
    case 'document': return <DocumentIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5 }} />
    default: return <ImageIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5 }} />
  }
}

interface ContentCardProps {
  item: ContentItem
  onEdit: (item: ContentItem) => void
  onDelete: (item: ContentItem) => void
  onStatusToggle?: (item: ContentItem, newStatus: 'active' | 'inactive') => void
}

const ContentCard: React.FC<ContentCardProps> = ({ item, onEdit, onDelete, onStatusToggle }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [viewDialogOpen, setViewDialogOpen] = useState(false)
  
  const open = Boolean(anchorEl)
  const isUploaded = item.type !== 'external_video' && (!!item.media_id || item.has_integration)
  const status = item.status

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    event.preventDefault()
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = (event?: React.MouseEvent | {}) => {
    if (event && 'stopPropagation' in event) {
      (event as React.MouseEvent).stopPropagation()
    }
    setAnchorEl(null)
  }

  const handleDelete = (event: React.MouseEvent) => {
    event.stopPropagation()
    setAnchorEl(null)
    onDelete(item)
  }

  const handleEdit = (event: React.MouseEvent) => {
    event.stopPropagation()
    setAnchorEl(null)
    onEdit(item)
  }

  return (
    <>
      <Card 
        onClick={() => setViewDialogOpen(true)}
        sx={{ 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column', 
          position: 'relative',
          cursor: 'pointer',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: (theme) => theme.shadows[1],
          border: '1px solid transparent',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: (theme) => theme.shadows[8],
            borderColor: (theme) => alpha(theme.palette.primary.main, 0.1),
            '& .preview-image': {
              transform: 'scale(1.05)',
            }
          }
        }}
      >
        {/* Preview Header */}
        <Box sx={{ 
          position: 'relative', 
          pt: '56.25%', 
            bgcolor: !(isUploaded && item.preview_url && item.type !== 'document') ? 'transparent' : 'action.hover',
            background: !(isUploaded && item.preview_url && item.type !== 'document') ? (theme) => `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.12)} 100%)` : undefined,
            overflow: 'hidden' 
          }}>
            {isUploaded && item.preview_url && item.type !== 'document' ? (
              item.type === 'video' || item.type === 'external_video' ? (
                <Box sx={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}>
                  <Box
                    className="preview-image"
                    component="img"
                    src={item.preview_url}
                    alt={item.title}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  />
                  <Box sx={{
                    position: 'absolute',
                    top: 0, left: 0, width: '100%', height: '100%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: 'rgba(0,0,0,0.2)',
                    transition: 'background-color 0.3s'
                  }}>
                    <IconButton 
                      sx={{ 
                        bgcolor: 'rgba(255,255,255,0.7)', 
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                        backdropFilter: 'blur(4px)',
                        color: 'primary.main',
                      }}
                    >
                      <PlayArrowIcon />
                    </IconButton>
                  </Box>
                </Box>
              ) : (
                <Box
                  className="preview-image"
                  component="img"
                  src={item.preview_url}
                  alt={item.title}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                />
              )
            ) : (
              <MediaPlaceholder item={item} isUploaded={isUploaded} size="small" />
            )}

            <Box sx={{ position: 'absolute', top: 12, left: 12 }}>
              <StatusBadge status={status} />
            </Box>

            <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
              <IconButton
                size="small"
                sx={{ 
                  bgcolor: (theme) => alpha(theme.palette.background.paper, 0.8),
                  backdropFilter: 'blur(4px)',
                  transition: 'all 0.2s ease',
                  '&:hover': { bgcolor: 'background.paper', transform: 'scale(1.1)' } 
                }}
                onClick={handleMenuOpen}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

        <CardContent sx={{ flexGrow: 1, p: 2.5, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Typography variant="subtitle1" fontWeight="600" noWrap sx={{ flexGrow: 1, textTransform: 'capitalize', pr: 1 }}>
              {item.title || 'Untitled'}
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            minHeight: '2.8em',
            mb: 2,
            lineHeight: 1.5
          }}>
            {item.description || 'No description provided.'}
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 'auto' }}>
            {item.tags?.map((tag, idx) => (
              <Chip 
                key={idx} 
                label={tag} 
                size="small" 
                sx={{ 
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                  color: 'primary.main',
                  border: 'none',
                  fontWeight: 500,
                  borderRadius: 1
                }} 
              />
            ))}
          </Box>
        </CardContent>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          onClick={(e) => e.stopPropagation()}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            elevation: 4,
            sx: { borderRadius: 2, mt: 1, minWidth: 160 }
          }}
        >
          <MenuItem
            onClick={(e) => {
              e.stopPropagation()
              if (item.has_integration) return
              setAnchorEl(null)
              const next = status === 'active' ? 'inactive' : 'active'
              onStatusToggle?.(item, next)
            }}
            disabled={item.has_integration}
            sx={{ py: 1.5 }}
          >
            <ListItemIcon>
              {status === 'active'
                ? <VisibilityOffIcon fontSize="small" />
                : <VisibilityIcon fontSize="small" />}
            </ListItemIcon>
            <Typography variant="body2">{status === 'active' ? 'Mark Inactive' : 'Mark Active'}</Typography>
          </MenuItem>
          <MenuItem onClick={handleEdit} sx={{ py: 1.5 }}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <Typography variant="body2">Edit Details</Typography>
          </MenuItem>
          <MenuItem onClick={handleDelete} sx={{ color: 'error.main', py: 1.5 }}>
            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
            <Typography variant="body2" color="error">Delete Item</Typography>
          </MenuItem>
        </Menu>
      </Card>

      {/* Quick View Dialog */}
      <ContentCardPreview
        item={item}
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        onEdit={onEdit}
      />
    </>
  )
}

export default ContentCard
